/**
 * Tweakable constants for dungeon gold rewards.
 * Gold is based on: (random 1%–5% of player gold at entry) × dungeon level × source multiplier.
 */

/** Minimum fraction of player gold for the base range (e.g. 0.01 = 1%). */
export const GOLD_PERCENT_MIN = 0.01;

/** Maximum fraction of player gold for the base range (e.g. 0.05 = 5%). */
export const GOLD_PERCENT_MAX = 0.05;

/** Multiplier for gold from defeating enemies. */
export const GOLD_ENEMY_MULTIPLIER = 7;

/** Multiplier for gold from opening chests. */
export const GOLD_CHEST_MULTIPLIER = 7;

/** Max enemy tier (1–10). Used to normalize enemy level: reward scales by enemyLevel/ENEMY_LEVEL_MAX. Chests omit enemyLevel so they get full (1). */
export const ENEMY_LEVEL_MAX = 10;

/**
 * Factor applied to rewards based on enemy tier (1–10). Reward scales linearly: level 1 = 10%, level 10 = 100%.
 * When not from an enemy (e.g. chest), omit enemyLevel so the factor is 1.
 */
function enemyLevelFactor(enemyLevel: number | undefined): number {
  const level = enemyLevel ?? ENEMY_LEVEL_MAX;
  const clamped = Math.max(1, Math.min(ENEMY_LEVEL_MAX, level));
  return clamped / ENEMY_LEVEL_MAX;
}

/**
 * Computes a random gold reward for the dungeon.
 * Formula: base (random percentMin–percentMax of playerGold) × dungeonLevel × sourceMultiplier × enemyLevelFactor.
 * For chests, do not pass enemyLevel so the factor is 1. For enemies, pass the enemy tier (1–10).
 *
 * @param playerGold - Player's gold when they entered the dungeon (or current gold).
 * @param dungeonLevel - Current dungeon level (≥ 1).
 * @param sourceMultiplier - GOLD_ENEMY_MULTIPLIER or GOLD_CHEST_MULTIPLIER.
 * @param enemyLevel - Enemy tier 1–10 (scales reward by enemyLevel/10). Omit for chests.
 */
export function computeDungeonGoldReward(
  playerGold: number,
  dungeonLevel: number,
  sourceMultiplier: number,
  enemyLevel?: number
): number {
  const level = Math.max(1, dungeonLevel);
  const minBase = playerGold * GOLD_PERCENT_MIN;
  const maxBase = playerGold * GOLD_PERCENT_MAX;
  const baseAmount = minBase + Math.random() * (maxBase - minBase);
  const factor = enemyLevelFactor(enemyLevel);
  return Math.max(50, Math.floor(baseAmount * level * sourceMultiplier * factor));
}
